import React from 'react';
import {useSearchParams} from 'react-router-dom';
import {atom, useRecoilValue} from 'recoil';
import {
    Grid,
    Typography,
    LinearProgress,
} from '@mui/material';
import PaginationCard from '../components/PaginationCard';
import ErrorCard from '../components/ErrorCard';
import QueryProcessor from '../components/QueryProcessor';
import BlockList from '../components/BlockList';
import {useSummary, useBlocks} from '../queries';
import {latestBlockState} from "../state";

const isFirstPageState = atom<boolean>({
    key: 'BlockView-is-first-page',
    default: true,
});

export interface BlockViewProps {
}

export function BlockView(props: BlockViewProps) {
    const [searchParams] = useSearchParams();

    let pageSize = Number(searchParams.get('ps'));
    if (isNaN(pageSize) || pageSize < 10) {
        pageSize = 15;
    }

    let pageNo = Number(searchParams.get('p'));
    if (isNaN(pageNo) || pageNo < 1) {
        pageNo = 1;
    }
    const latestBlock = useRecoilValue(latestBlockState);
    const summaryQuery = useSummary();

    if (latestBlock.height >=0){
        return <ViewBlocks latestHeight={latestBlock.height} pageSize={pageSize} pageNo={pageNo}/>;
    }

    return (
        <QueryProcessor
            query={summaryQuery}
            dataValidator={(data: any) => data.type === 'summary'}
            renderer={(data: any) => {
                return <ViewBlocks
                    latestHeight={data.payload.latestBlockHeight}
                    pageSize={pageSize}
                    pageNo={pageNo}
                />;
            }}
        />
    );
}

interface ViewBlocksProps {
    latestHeight: number;
    pageSize: number;
    pageNo: number;
}

function ViewBlocks({latestHeight,pageSize, pageNo}: ViewBlocksProps) {

    const heightEnd =  latestHeight - (pageNo - 1) * pageSize;
    const heightBegin = Math.max(0, heightEnd - pageSize + 1);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography component={'span'} variant={'h2'}>
                    Blocks{' '}
                </Typography>
                <Typography component={'span'} variant={'h5'} color={'textSecondary'}>
                    #{heightBegin} to #{heightEnd} (Total of {(latestHeight + 1).toLocaleString()} blocks)
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <PaginationCard totalItems={latestHeight + 1} pageSize={pageSize} pageNo={pageNo}>
                    <ViewBlocksContent heightBegin={heightBegin} heightEnd={heightEnd}/>
                </PaginationCard>
            </Grid>
        </Grid>
    );
}

interface ViewBlocksContentProps {
    heightBegin: number;
    heightEnd: number;
}

function ViewBlocksContent({heightBegin, heightEnd}: ViewBlocksContentProps) {
    const fetchCount = 100;
    const startForHeightBegin = Math.floor(heightBegin / fetchCount) * fetchCount;
    const startForHeightEnd = Math.floor(heightEnd / fetchCount) * fetchCount;
    const isFirstPage = useRecoilValue(isFirstPageState);

    const queryOptions = {
        staleTime: isFirstPage ? 1000 * 30 : 1000 * 300,
        refetchOnMount: isFirstPage,
        refetchOnWindowFocus: isFirstPage,
    };
    const qs = [
        useBlocks(startForHeightBegin, fetchCount, {
            enabled: startForHeightBegin !== startForHeightEnd,
            ...queryOptions,
        }),
        useBlocks(startForHeightEnd, fetchCount, {
            ...queryOptions,
        }),
    ];

    if (qs.some((q) => q.isLoading) || qs.some((q) => q.isRefetching)) {
        return <LinearProgress color={'warning'}/>;
    }

    if (qs.some((q) => q.isError) || qs.some((q) => !q.isSuccess)) {
        return (
            <ErrorCard enableGoBack={true}>
                <Typography variant={'h4'}>Failed fetching blocks data.</Typography>
            </ErrorCard>
        );
    }

    const data =
        startForHeightBegin === startForHeightEnd ? qs[0].data.payload : [...qs[0].data.payload, ...qs[1].data.payload];


    return <BlockList data={data.filter((d: any) => heightBegin <= d.height && d.height <= heightEnd)}/>;
}


export default BlockView;
